import { useEffect, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal, ModalFooter, Button, Input, Select } from '@/components/common';
import {
  useActiveChannelTypes,
  useCreateChannel,
  useUpdateChannel,
  useUnlinkedPospOptions,
} from '@/hooks/useChannels';
import {
  channelIdOf,
  type CreateChannelPayload,
  type UnifiedChannelRow,
  type UpdateChannelPayload,
} from '@/services/channels';
// Imported by path rather than through services/index.ts, which is outside this
// task's file domain. Same store the channel detail page and the agent's own
// My Portal read — item 5 adds a way IN at create time, not a second store.
import { CHANNEL_KYC_DOCUMENT_TYPES, channelKycService } from '@/services/kycDocuments';
import { getErrorMessage } from '@/services/api';
import {
  validateAddChannel,
  hasErrors,
  resolveBaseType,
  customChannelTypeId,
  CUSTOM_TYPE_PREFIX,
  type AddChannelForm,
  type AddChannelErrors,
} from './addChannelValidation';

/** B7 (15 Sep QA batch): the server may save a POSP channel WITHOUT a portal
 *  login, because its email or phone already belongs to another login (a
 *  customer, say). The channel really was created, so this is not an error --
 *  but the broker must be told, or "Channel added" reads as a working login. */
function announceLoginWarning(response: unknown) {
  const warning = (response as { login_warning?: string } | undefined)?.login_warning;
  if (warning) toast(warning, { duration: 12000 });
}

const BUILT_IN_TYPE_OPTIONS = [
  { value: 'POSP', label: 'POSP' },
  { value: 'ADVERTISER', label: 'Advertiser' },
  { value: 'CONSOLIDATOR', label: 'Consolidator' },
];

const BASE_TYPE_LABELS: Record<string, string> = {
  POSP: 'POSP',
  ADVERTISER: 'Advertiser',
  CONSOLIDATOR: 'Consolidator',
  AGENT: 'Agent',
  BRANCH: 'Branch',
  PARTNER: 'Partner',
  OTHER: 'Other',
};

/** What a channel of this base type actually gets, in the user's terms. Shown
 *  under the picker so "custom type" never reads as a cosmetic label. */
const BASE_TYPE_EFFECT: Record<string, string> = {
  POSP: 'provisions an agent login and is paid through the POSP waterfall',
  ADVERTISER: "earns a cut of its POSPs' policies and gets its own report widget",
  CONSOLIDATOR: 'is paid from its own payout grid and gets its own report widget',
  AGENT: 'is paid from its own payout grid and reports under Agent',
  BRANCH: 'is paid from its own payout grid and reports under Branch',
  PARTNER: 'is paid from its own payout grid and reports under Partner',
  OTHER: 'is paid from its own payout grid and reports under Other',
};

interface AddChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, the modal edits this row's channel instead of creating a new one. */
  channel?: UnifiedChannelRow;
}

// Point 10 (QA round 3): mirrors the server default (DEFAULT_CHANNEL_PASSWORD
// in apps.brokers.serializers) so the admin sees the credential they're about
// to hand the agent. The agent must change it on first login
// (must_change_password) -- this is never a real, kept-secret password.
const DEFAULT_CHANNEL_PASSWORD = 'Test@1234';

const EMPTY: AddChannelForm = { channel_type: 'POSP', password: DEFAULT_CHANNEL_PASSWORD, subchannels: [] };

/** One KYC document that did not make it, and why. Item 5's whole point: a
 *  document that failed is NAMED, and the channel it belongs to still exists. */
interface FailedDocument {
  documentType: string;
  label: string;
  fileName: string;
  message: string;
}

export function AddChannelModal({ isOpen, onClose, channel }: AddChannelModalProps) {
  const isEdit = Boolean(channel);
  const [form, setForm] = useState<AddChannelForm>(EMPTY);
  const [errors, setErrors] = useState<AddChannelErrors>({});
  const createChannel = useCreateChannel();
  const updateChannel = useUpdateChannel();
  const queryClient = useQueryClient();

  // ── Item 5: KYC documents picked BEFORE the channel exists ───────────────
  //
  // `ChannelDocument.channel` is a non-nullable FK and the upload endpoint
  // resolves the channel through a scoped queryset, so a document genuinely
  // cannot precede the channel row. The shape is therefore the one already
  // proven by profile-picture-on-create in CustomerFormPage: hold the files
  // here, create the channel, then POST each file against the id that came
  // back.
  //
  // The failure mode this must NOT have is the platform's signature bug — an
  // action reporting success with nothing behind it. So: the channel is never
  // rolled back (it was created, saying otherwise would be a lie), every
  // document that failed is named with its reason, the modal STAYS OPEN on a
  // partial failure, and Retry posts only the failures against the channel
  // that already exists.
  const [documents, setDocuments] = useState<Record<string, File>>({});
  const [createdChannelId, setCreatedChannelId] = useState<string | null>(null);
  const [failedDocuments, setFailedDocuments] = useState<FailedDocument[]>([]);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  // Board #252: the broker's own channel types, offered alongside the built-ins
  // in this very dropdown. Invalidated by useCreateChannelType, so a type made
  // on the Channel Types page appears here without a reload.
  const { data: channelTypesPage } = useActiveChannelTypes();
  const customTypes = channelTypesPage?.results ?? [];

  const typeOptions = [
    ...BUILT_IN_TYPE_OPTIONS,
    ...customTypes.map((t) => ({
      value: `${CUSTOM_TYPE_PREFIX}${t.id}`,
      label: `${t.name} (custom)`,
    })),
  ];

  const selectedCustomType = customTypes.find(
    (t) => t.id === customChannelTypeId(form.channel_type),
  );
  const baseType = resolveBaseType(form);

  // Board #267. An agent whose owning channel was soft-deleted has no channel
  // record, cannot be edited from the channels list, and earns nothing through
  // the channel payout grid. This select is how it gets one back. Only fetched
  // for a POSP-type create -- it is meaningless anywhere else.
  const isPospCreate = !isEdit && baseType === 'POSP';
  const { data: unlinkedPosps } = useUnlinkedPospOptions(isPospCreate);
  const linkedPospId = form.posp?.trim() ?? '';

  // Prefill from the row when opening in edit mode; reset when opening for create.
  useEffect(() => {
    if (!isOpen) return;
    if (channel) {
      setForm({
        // type_label is the Channel.channel_type value ('POSP', 'ADVERTISER', …)
        // for channel rows, and 'POSP' for POSP rows. It drives validation only —
        // it is never submitted (read-only server-side).
        channel_type: channel.type_label,
        name: channel.name,
        code: channel.code,
        email: channel.email ?? '',
        phone: channel.phone ?? '',
        commission_percentage: channel.commission_percentage ?? '',
        subchannels: [],
      });
    } else {
      setForm(EMPTY);
    }
    setErrors({});
    // Item 5: a reopened modal starts from nothing. `close()` already clears
    // these, but the parent controls `isOpen` and can hide the modal without
    // going through it -- and a stale created-channel id would then disable
    // Create for the NEXT channel.
    setDocuments({});
    setCreatedChannelId(null);
    setFailedDocuments([]);
    setUploadedCount(0);
    setIsUploading(false);
  }, [channel, isOpen]);

  // The custom types can finish loading AFTER a value is picked (or a stale
  // base could linger from a previous pick), so re-derive rather than trusting
  // whatever the change handler stored.
  useEffect(() => {
    if (!selectedCustomType) return;
    if (form.custom_base_type === selectedCustomType.base_type) return;
    setForm((f) => ({ ...f, custom_base_type: selectedCustomType.base_type }));
  }, [selectedCustomType, form.custom_base_type]);

  // Advertiser behaviour follows the RESOLVED base type: an advertiser-based
  // custom type must offer subchannels exactly like the built-in does.
  const isAdvertiser = baseType === 'ADVERTISER';

  const set = (key: keyof AddChannelForm, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setSub = (i: number, key: string, value: string) =>
    setForm((f) => {
      const subs = [...(f.subchannels ?? [])];
      subs[i] = { ...subs[i], [key]: value };
      return { ...f, subchannels: subs };
    });

  const addSub = () => setForm((f) => ({ ...f, subchannels: [...(f.subchannels ?? []), {}] }));
  const removeSub = (i: number) =>
    setForm((f) => ({ ...f, subchannels: (f.subchannels ?? []).filter((_, j) => j !== i) }));

  const reset = () => {
    setForm(EMPTY);
    setErrors({});
    setDocuments({});
    setCreatedChannelId(null);
    setFailedDocuments([]);
    setUploadedCount(0);
    setIsUploading(false);
    fileInputs.current = {};
  };
  const close = () => {
    reset();
    onClose();
  };

  const setDocument = (documentType: string, file: File | null) =>
    setDocuments((current) => {
      const next = { ...current };
      if (file) next[documentType] = file;
      else delete next[documentType];
      return next;
    });

  /**
   * Upload the picked files against a channel that already exists.
   *
   * Sequential, not `Promise.all`: OCR-free though this endpoint is, a phone
   * photo per slot on a slow uplink is still five multipart bodies, and one
   * rejected file must not cancel the rest. Every outcome is recorded — the
   * count that landed and the ones that did not, each with the server's own
   * message — and NOTHING is swallowed.
   */
  const uploadDocuments = async (channelId: string, entries: Array<[string, File]>) => {
    setIsUploading(true);
    const failures: FailedDocument[] = [];
    let succeeded = 0;

    for (const [documentType, file] of entries) {
      try {
        await channelKycService.upload({ channel: channelId, document_type: documentType, file });
        succeeded += 1;
        // Drop it from the pending set as soon as it is safely stored, so a
        // Retry never re-posts a document that already landed.
        setDocument(documentType, null);
      } catch (error) {
        failures.push({
          documentType,
          label:
            CHANNEL_KYC_DOCUMENT_TYPES.find((slot) => slot.value === documentType)?.label ?? documentType,
          fileName: file.name,
          message: getErrorMessage(error),
        });
      }
    }

    setUploadedCount((n) => n + succeeded);
    setFailedDocuments(failures);
    setIsUploading(false);
    // The detail page reads these rows; without this the uploads succeeded and
    // the page that shows them did not move.
    if (succeeded > 0) {
      queryClient.invalidateQueries({ queryKey: ['channelKycDocuments', channelId] });
    }
    if (failures.length === 0) {
      toast.success(
        succeeded === 1 ? '1 document uploaded.' : `${succeeded} documents uploaded.`
      );
      close();
    }
  };

  const retryFailedDocuments = () => {
    if (!createdChannelId) return;
    const entries = failedDocuments
      .map((failure) => [failure.documentType, documents[failure.documentType]] as const)
      .filter((entry): entry is readonly [string, File] => Boolean(entry[1]));
    if (entries.length === 0) return;
    setFailedDocuments([]);
    void uploadDocuments(createdChannelId, entries as Array<[string, File]>);
  };

  const handleSubmit = () => {
    const e = validateAddChannel(form);
    setErrors(e);
    if (hasErrors(e)) return;

    if (isEdit && channel) {
      // THE ID TRAP: a POSP row's `id` is the POSP id. Only channel_id addresses
      // the channel record, so patch that and nothing else.
      const channelId = channelIdOf(channel);
      if (!channelId) {
        setErrors({ name: 'This POSP has no channel record and cannot be edited here.' });
        return;
      }

      // Send only what actually changed. A POSP-backed channel MIRRORS every field
      // it receives onto the agent's User row (name → first_name, email, phone) and
      // its catch-all payout rate, so resending an untouched `name` rewrites
      // first_name with the channel's display name and duplicates the surname in
      // full_name. A PATCH should carry the edit, not the whole form.
      const data: UpdateChannelPayload = {};
      const name = form.name!.trim();
      const code = form.code?.trim() ?? '';
      const email = form.email?.trim() ?? '';
      const phone = form.phone?.trim() ?? '';
      const pct = form.commission_percentage ?? '';
      if (name !== channel.name) data.name = name;
      if (code !== (channel.code ?? '')) data.code = code;
      if (email !== (channel.email ?? '')) data.email = email;
      if (phone !== (channel.phone ?? '')) data.phone = phone;
      // Cleared field → null, which clears the channel's default share (the column
      // is nullable); '' would be rejected as a bad decimal.
      if (pct !== (channel.commission_percentage ?? '')) data.commission_percentage = pct || null;

      if (Object.keys(data).length === 0) {
        close();
        return;
      }

      updateChannel.mutate(
        { channelId, data },
        {
          onSuccess: (updated) => {
            announceLoginWarning(updated);
            close();
          },
        },
      );
      return;
    }

    // Board #252: a custom type is sent as `type_ref` and NOTHING else. The
    // server derives channel_type from that type's base_type -- the client must
    // not restate it, or the two could disagree and the label would stop
    // matching the behaviour.
    const customTypeId = customChannelTypeId(form.channel_type);

    const payload: CreateChannelPayload = {
      name: form.name!.trim(),
      code: form.code?.trim() || undefined,
      channel_type: customTypeId ? undefined : form.channel_type!,
      type_ref: customTypeId,
      email: form.email?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      // Only meaningful when the server actually provisions a login (a POSP
      // BASE type with an email or phone); harmless to send otherwise since a
      // plain channel row has no `password` column to write to.
      password:
        baseType === 'POSP' && !linkedPospId ? form.password?.trim() || undefined : undefined,
      // Board #267: links the existing agent. The server then creates no User
      // and no POSP, whatever email/phone were typed.
      posp: linkedPospId || undefined,
      commission_percentage: form.commission_percentage || undefined,
      subchannels:
        isAdvertiser && form.subchannels?.length
          ? form.subchannels.map((s) => ({
              name: s.name!.trim(),
              code: s.code?.trim() || undefined,
              email: s.email?.trim() || undefined,
              phone: s.phone?.trim() || undefined,
              commission_percentage: s.commission_percentage || undefined,
            }))
          : undefined,
    };

    createChannel.mutate(payload, {
      onSuccess: (created) => {
        announceLoginWarning(created);
        const pending = Object.entries(documents);
        if (pending.length === 0) {
          close();
          return;
        }
        // The channel EXISTS from here on. Whatever happens to the files, this
        // modal must never suggest otherwise — hence the id is held, not the
        // form, and `close()` is called only when there is nothing outstanding.
        setCreatedChannelId(created.id);
        void uploadDocuments(created.id, pending);
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      // Escape and a backdrop click go through here too. Dismissing mid-upload
      // would unmount the only place a failed document is ever reported, so the
      // modal holds until the uploads have finished answering.
      onClose={isUploading ? () => {} : close}
      title={isEdit ? 'Edit Channel' : 'Add Channel'}
      size="lg"
    >
      <div className="space-y-4">
        {/* B7: a POSP channel saved without a portal login (its email or phone
            belonged to another login). The create toast is gone by the time the
            broker opens this, so the modal says it again, where the fix is made.
            `has_login` is only on unified rows; false means "none", undefined
            means the row did not say. */}
        {isEdit &&
          channel?.type_label === 'POSP' &&
          (channel as { has_login?: boolean | null }).has_login === false && (
            <p role="note" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              This channel has no portal login. Its email or phone already belongs to another login on the
              platform. Enter an email (and phone) not used by any other login and save to create one.
            </p>
          )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* id/name given explicitly: without one, `Input` renders a <label>
              with no htmlFor, so the field has no accessible name at all —
              invisible to a screen reader and unreachable by a label query. */}
          <Input id="channel-name" name="channel-name" label="Name" required value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} error={errors.name} />
          <Input id="channel-code" name="channel-code" label="Code" required value={form.code ?? ''} onChange={(e) => set('code', e.target.value)} error={errors.code} />
          <Input id="channel-email" name="channel-email" label="Email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} error={errors.email} />
          <Input id="channel-phone" name="channel-phone" label="Phone" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
          {/* Board #267: no login is provisioned when an existing agent is
              linked, so offering a password here would be a control with
              nothing behind it. */}
          {isPospCreate && !linkedPospId && (
            <Input
              label="Login Password"
              value={form.password ?? ''}
              onChange={(e) => set('password', e.target.value)}
              helperText="Defaults to Test@1234 — the agent must change it on first login."
            />
          )}
          {isEdit ? (
            // Channel type is read-only server-side (retyping a POSP-backed channel
            // is rejected by Channel.save()), so it is shown, not offered.
            <Input
              label="Channel Type"
              value={channel?.type_display ?? form.channel_type ?? ''}
              disabled
              readOnly
              helperText={
                channel && channel.type_display !== channel.type_label
                  ? `Behaves as ${BASE_TYPE_LABELS[channel.type_label] ?? channel.type_label}.`
                  : undefined
              }
            />
          ) : (
            <Select
              label="Channel Type"
              required
              options={typeOptions}
              value={form.channel_type}
              onChange={(e) => {
                const value = e.target.value;
                const picked = customTypes.find((t) => t.id === customChannelTypeId(value));
                // Set both in ONE update: a custom value whose base arrived in a
                // later render would validate against `undefined` in between.
                const nextBase = picked ? picked.base_type : value;
                setForm((f) => ({
                  ...f,
                  channel_type: value,
                  custom_base_type: picked?.base_type,
                  // Board #267: a linked agent only makes sense on a POSP
                  // channel. Leaving a stale id in the form would post it and
                  // earn a 400 the user never asked for.
                  posp: nextBase === 'POSP' ? f.posp : undefined,
                }));
              }}
              error={errors.channel_type}
              helperText={
                selectedCustomType
                  ? `Behaves as ${BASE_TYPE_LABELS[selectedCustomType.base_type] ?? selectedCustomType.base_type}: ${BASE_TYPE_EFFECT[selectedCustomType.base_type] ?? 'is paid from its own payout grid'}.`
                  : undefined
              }
            />
          )}
          <Input
            label="Commission %"
            type="number"
            value={form.commission_percentage ?? ''}
            onChange={(e) => set('commission_percentage', e.target.value)}
            error={errors.commission_percentage}
          />
          {/* Board #267: restored, and now actually wired. The list is the
              agents with NO channel record; an agent that already has one is
              edited there, so it is deliberately absent here. */}
          {isPospCreate && (
            <Select
              label="Linked Agent / POSP"
              value={form.posp ?? ''}
              onChange={(e) => set('posp', e.target.value)}
              options={(unlinkedPosps ?? []).map((p) => ({
                value: p.id,
                label: p.code ? `${p.name} (${p.code})` : p.name,
              }))}
              placeholder="None — create a new agent and login"
              error={errors.posp}
              helperText={
                linkedPospId
                  ? 'This channel will represent that existing agent. No new login is created and the agent\u2019s own login is untouched.'
                  : (unlinkedPosps ?? []).length === 0
                    ? 'No unlinked agents — every agent already has a channel, edit it there.'
                    : 'Link an agent that lost its channel instead of creating a new one.'
              }
            />
          )}
        </div>

        {/* Item 5: KYC documents, picked here and uploaded the moment the
            channel has an id. Create-only — once the channel exists its
            documents are managed on the channel detail page (and on the
            agent's own My Portal), against the SAME rows. */}
        {!isEdit && (
          <div className="border-t border-gray-200 pt-4">
            <div className="mb-2">
              <h4 className="text-sm font-semibold text-gray-900">KYC Documents</h4>
              <p className="text-xs text-gray-500">
                Optional now — attach what you have and the rest can be added on the channel&apos;s
                page. Files upload after the channel is created, so a failed upload never loses the
                channel.
              </p>
            </div>

            <div className="space-y-2">
              {CHANNEL_KYC_DOCUMENT_TYPES.map((slot) => {
                const picked = documents[slot.value];
                const failure = failedDocuments.find((f) => f.documentType === slot.value);
                return (
                  <div
                    key={slot.value}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-2 ${
                      failure ? 'border-red-300 bg-red-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{slot.label}</p>
                      {picked ? (
                        <p className="truncate text-xs text-gray-500">{picked.name}</p>
                      ) : (
                        <p className="text-xs text-gray-400">No file chosen</p>
                      )}
                      {failure && (
                        <p className="text-xs font-medium text-red-700">{failure.message}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,.pdf"
                        aria-label={`Choose ${slot.label}`}
                        ref={(el) => {
                          fileInputs.current[slot.value] = el;
                        }}
                        onChange={(e) => setDocument(slot.value, e.target.files?.[0] ?? null)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
                        disabled={isUploading}
                        onClick={() => fileInputs.current[slot.value]?.click()}
                      >
                        {picked ? 'Change' : 'Choose file'}
                      </Button>
                      {picked && (
                        <button
                          type="button"
                          aria-label={`Remove ${slot.label}`}
                          className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                          disabled={isUploading}
                          onClick={() => {
                            setDocument(slot.value, null);
                            const input = fileInputs.current[slot.value];
                            if (input) input.value = '';
                          }}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* A partial failure is stated, not swallowed. The channel is real
                and is named as real; the documents that did not land are listed
                with the server's own message and can be retried against it. */}
            {createdChannelId && failedDocuments.length > 0 && (
              <div
                role="alert"
                className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    The channel was created. {uploadedCount > 0 ? `${uploadedCount} document${uploadedCount === 1 ? '' : 's'} uploaded, ` : ''}
                    {failedDocuments.length} could not be uploaded.
                  </p>
                  <ul className="mt-1 list-inside list-disc text-xs text-red-700">
                    {failedDocuments.map((failure) => (
                      <li key={failure.documentType}>
                        {failure.label} ({failure.fileName}) — {failure.message}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-red-600">
                    Retry below, or close and upload them from the channel&apos;s page. Nothing about
                    the channel itself was lost.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subchannels provision new POSPs — a create-time concern only. */}
        {isAdvertiser && !isEdit && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900">POSP Subchannels</h4>
              <Button variant="outline" size="sm" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={addSub}>
                Add Subchannel
              </Button>
            </div>
            {(form.subchannels ?? []).length === 0 && (
              <p className="text-sm text-gray-500">
                Add one or more POSPs that sell under this advertiser.
              </p>
            )}
            <div className="space-y-3">
              {(form.subchannels ?? []).map((s, i) => (
                <div key={i} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">POSP #{i + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeSub(i)}
                      className="text-gray-400 hover:text-red-600"
                      aria-label="Remove subchannel"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input label="Name" value={s.name ?? ''} onChange={(e) => setSub(i, 'name', e.target.value)} error={errors.subchannels?.[i]?.name} />
                    <Input label="Code" value={s.code ?? ''} onChange={(e) => setSub(i, 'code', e.target.value)} error={errors.subchannels?.[i]?.code} />
                    <Input label="Email" type="email" value={s.email ?? ''} onChange={(e) => setSub(i, 'email', e.target.value)} error={errors.subchannels?.[i]?.email} />
                    <Input label="Phone" value={s.phone ?? ''} onChange={(e) => setSub(i, 'phone', e.target.value)} />
                    <Input
                      label="Commission %"
                      type="number"
                      value={s.commission_percentage ?? ''}
                      onChange={(e) => setSub(i, 'commission_percentage', e.target.value)}
                      error={errors.subchannels?.[i]?.commission_percentage}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={close} disabled={isUploading}>
          {createdChannelId ? 'Close' : 'Cancel'}
        </Button>
        {/* Once the channel exists the primary action is no longer "create" —
            offering it again would invite a second channel for the same agent.
            From here the only outstanding work is the documents. */}
        {createdChannelId ? (
          <Button
            onClick={retryFailedDocuments}
            isLoading={isUploading}
            disabled={isUploading || failedDocuments.length === 0}
          >
            {isUploading
              ? 'Uploading documents…'
              : `Retry ${failedDocuments.length} document${failedDocuments.length === 1 ? '' : 's'}`}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            isLoading={isEdit ? updateChannel.isPending : createChannel.isPending}
          >
            {isEdit ? 'Save Changes' : 'Create Channel'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
